import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type PlanTier = "free" | "pro" | "enterprise";
export type BillingStatus = "none" | "active" | "trialing" | "past_due" | "canceled" | "incomplete";

export interface Entitlements {
  planId: string | null;
  planTier: PlanTier;
  planLabel: string;
  features: string[];
  quotas: Record<string, { limit: number | null; used: number }>;
  billingStatus: BillingStatus;
  trialEndsAt: string | null;
  updatedAt: string;
}

export interface UseEntitlementsResult {
  status: "idle" | "loading" | "ready" | "error";
  entitlements: Entitlements | null;
  error: Error | null;
  refresh: () => void;
  hasFeature: (featureKey: string) => boolean;
  hasPlanTier: (requiredTier: PlanTier) => boolean;
  checkQuota: (quotaKey: string, requiredUnits?: number) => boolean;
}

const TIER_HIERARCHY: Record<PlanTier, number> = {
  free: 0,
  pro: 1,
  enterprise: 2,
};

const DEFAULT_ENTITLEMENTS: Entitlements = {
  planId: null,
  planTier: "free",
  planLabel: "Free",
  features: ["community_access", "resource_library"],
  quotas: {},
  billingStatus: "none",
  trialEndsAt: null,
  updatedAt: new Date().toISOString(),
};

export function useEntitlements(): UseEntitlementsResult {
  const { user } = useAuth();

  const {
    data: entitlements,
    isLoading,
    error,
    refetch,
    isError,
  } = useQuery({
    queryKey: ["entitlements", user?.id],
    queryFn: async (): Promise<Entitlements> => {
      if (!user?.id) return DEFAULT_ENTITLEMENTS;

      // First check for active subscription - this is the source of truth
      const { data: subscription } = await supabase
        .from("user_subscriptions")
        .select(`
          *,
          plan:membership_plans(id, name, features)
        `)
        .eq("user_id", user.id)
        .maybeSingle();

      // If user has an active/trialing subscription, derive tier from it
      if (subscription?.plan && ["active", "trialing"].includes(subscription.status || "")) {
        const planName = subscription.plan.name?.toLowerCase() || "";
        const tier: PlanTier = planName.includes("pro") ? "pro" : planName.includes("enterprise") ? "enterprise" : "free";
        
        // Get plan features
        const { data: planFeatures } = await supabase
          .from("plan_features")
          .select("feature_key")
          .eq("plan_id", subscription.plan.id);

        const features = planFeatures?.map(f => f.feature_key) || [];

        // Also check for overrides
        const { data: overrides } = await supabase
          .from("entitlement_overrides")
          .select("feature_key, granted, expires_at")
          .eq("user_id", user.id);

        const activeOverrides = overrides?.filter(o => 
          o.granted && (!o.expires_at || new Date(o.expires_at) > new Date())
        ).map(o => o.feature_key) || [];

        const allFeatures = [...new Set([...features, ...activeOverrides])];

        return {
          planId: subscription.plan.id,
          planTier: tier,
          planLabel: subscription.plan.name || "Free",
          features: allFeatures,
          quotas: {},
          billingStatus: (subscription.status as BillingStatus) || "none",
          trialEndsAt: null,
          updatedAt: subscription.updated_at || new Date().toISOString(),
        };
      }

      // Fallback: Try to get entitlement snapshot
      const { data: snapshot } = await supabase
        .from("entitlement_snapshots")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (snapshot) {
        return {
          planId: snapshot.plan_id,
          planTier: snapshot.plan_tier as PlanTier,
          planLabel: snapshot.plan_tier === "pro" ? "Pro" : snapshot.plan_tier === "enterprise" ? "Enterprise" : "Free",
          features: Array.isArray(snapshot.features) ? snapshot.features as string[] : [],
          quotas: (snapshot.quotas as Record<string, { limit: number | null; used: number }>) || {},
          billingStatus: snapshot.billing_status as BillingStatus,
          trialEndsAt: snapshot.trial_ends_at,
          updatedAt: snapshot.updated_at,
        };
      }

      return DEFAULT_ENTITLEMENTS;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  const hasFeature = (featureKey: string): boolean => {
    if (!entitlements) return false;
    return entitlements.features.includes(featureKey);
  };

  const hasPlanTier = (requiredTier: PlanTier): boolean => {
    if (!entitlements) return false;
    return TIER_HIERARCHY[entitlements.planTier] >= TIER_HIERARCHY[requiredTier];
  };

  const checkQuota = (quotaKey: string, requiredUnits: number = 1): boolean => {
    if (!entitlements) return false;
    const quota = entitlements.quotas[quotaKey];
    if (!quota) return true; // No quota defined = unlimited
    if (quota.limit === null) return true; // Unlimited
    return quota.used + requiredUnits <= quota.limit;
  };

  return {
    status: !user?.id ? "idle" : isLoading ? "loading" : isError ? "error" : "ready",
    entitlements: entitlements || null,
    error: error as Error | null,
    refresh: refetch,
    hasFeature,
    hasPlanTier,
    checkQuota,
  };
}

// Helper hook for single feature check
export function usePlanGate(featureKey: string): {
  isAllowed: boolean;
  isLoading: boolean;
  planTier: PlanTier;
} {
  const { entitlements, status, hasFeature } = useEntitlements();

  return {
    isAllowed: hasFeature(featureKey),
    isLoading: status === "loading",
    planTier: entitlements?.planTier || "free",
  };
}
